import { constants } from '../constants/global.constants';
import { getFirebase } from 'react-redux-firebase';
import { alertActions } from './alert.actions';
import {
  refUpdate,
  refSave,
  getDbFromType,
  getLeastPlannerId,
  clean
} from './entity.actions';
import { ActionCreator, AnyAction, Action } from 'redux';
import { RootState } from '../modules';
import { ThunkDispatch } from 'redux-thunk';
import { getProjectId } from '../selectors/project.selector';
import { Trade } from '../models/Trade';
import {
  getSelectedPhasePlanId,
  getSelectedPhasePlanTags
} from '../selectors/phaseplan.selector';
import { PhaseTag } from '../models/PhaseTag';
import { dialogActions } from './dialog.actions';
import { isSelectMultiPressed } from '../selectors/util.selector';
import { batch } from 'react-redux';
import { homeActions } from './home.actions';
import { Constraint } from '../models/Constraint';
import { ColumnType } from '../components/PhasePlan/Column';
import { TagType, SelectedTag } from '../models/ TagType';
import { lookaheadActions } from './lookahead.actions';
import { getSelectedLookaheadId } from '../selectors/lookahead.selector';
import { PullOrPush } from '../utils/PullOrPush';
import { PullInfo } from '../modules/lookahead.reducer';
import bugsnagClient from '../utils/BugSnagClient';

const sh = require('shorthash');

export const PHASE_PLAN_SELECTED = 'PHASE_PLAN_SELECTED';
export const LOOKAHEAD_OFF_DAY_SELECTED = 'LOOKAHEAD_OFF_DAY_SELECTED';
export const LOOKAHEAD_OFF_DAY_CLEAR = 'LOOKAHEAD_OFF_DAY_CLEAR';
export const LOOKAHEAD_LENGTH_SELECTED = 'LOOKAHEAD_LENGTH_SELECTED';
export const LOOKAHEAD_LENGTH_CLEAR = 'LOOKAHEAD_LENGTH_CLEAR';

export const PHASE_PLAN_TAG_SELECTED = 'PHASE_PLAN_TAG_SELECTED';
export const PHASE_PLAN_TAGS_SELECTED = 'PHASE_PLAN_TAGS_SELECTED';
export const PHASE_PLAN_TAGS_CLEARED = 'PHASE_PLAN_TAGS_CLEARED';
export const PHASE_PLAN_HISTOGRAM_TOGGLE = 'PHASE_PLAN_HISTOGRAM_TOGGLE';
export const PHASE_PLAN_FILTER_BY_TRADE = 'PHASE_PLAN_FILTER_BY_TRADE';
export const LOOKAHEAD_OFF_DAY_BULK_SELECTED = 'LOOKAHEAD_OFF_DAY_BULK_SELECTED'


interface PhasePlanTagSelectedAction extends Action {
  type: 'PHASE_PLAN_TAG_SELECTED';
  payload: {
    tagId: string;
  };
}

interface PhasePlanTagsSelectedAction extends Action {
  type: 'PHASE_PLAN_TAGS_SELECTED';
  payload: {
    tags: Record<string, SelectedTag>;
  };
}

interface PhasePlanTagsClearedAction extends Action {
  type: 'PHASE_PLAN_TAGS_CLEARED';
  payload: {};
}

interface LookaheadOffDaySelectedAction extends Action {
  type: 'LOOKAHEAD_OFF_DAY_SELECTED';
  payload: {
    selectedDay: number;
  };
}

interface LookaheadOffDayBulkSelectedAction extends Action {
  type: 'LOOKAHEAD_OFF_DAY_BULK_SELECTED';
  payload: {
    selectedDays: number[];
  };
}

interface LookaheadLengthSelectedAction extends Action {
  type: 'LOOKAHEAD_LENGTH_SELECTED';
  payload: {
    length: number;
  };
}

interface LookaheadLengthClearAction extends Action {
  type: 'LOOKAHEAD_LENGTH_CLEAR';
  payload: {};
}

interface LookaheadOffDayClearAction extends Action {
  type: 'LOOKAHEAD_OFF_DAY_CLEAR';
  payload: {};
}

interface PhasePlanSelectedAction extends Action {
  type: 'PHASE_PLAN_SELECTED';
  payload: {
    selectedPhasePlan: string;
  };
}

interface PhasePlanHistogramToggle extends Action {
  type: 'PHASE_PLAN_HISTOGRAM_TOGGLE';
  payload: {};
}

interface PhasePlanFilterByTradeAction extends Action {
  type: 'PHASE_PLAN_FILTER_BY_TRADE';
  payload: {
    trade: Trade | null;
  };
}

const phasePlanFilterByTrade: ActionCreator<PhasePlanFilterByTradeAction> = (
  trade: Trade | null
) => ({
  type: PHASE_PLAN_FILTER_BY_TRADE,
  payload: {
    trade: trade
  }
});

const lookaheadOffDayClear: ActionCreator<LookaheadOffDayClearAction> = (
  index: number
) => ({
  type: LOOKAHEAD_OFF_DAY_CLEAR,
  payload: {}
});

const lookaheadOffDaySelected: ActionCreator<LookaheadOffDaySelectedAction> = (
  index: number
) => ({
  type: LOOKAHEAD_OFF_DAY_SELECTED,
  payload: {
    selectedDay: index
  }
});

const lookaheadOffDayBulkSelected: ActionCreator<LookaheadOffDayBulkSelectedAction> = (
  index: number[]
) => ({
  type: LOOKAHEAD_OFF_DAY_BULK_SELECTED,
  payload: {
    selectedDays: index
  }
});

const lookaheadLengthClear: ActionCreator<LookaheadLengthClearAction> = () => ({
  type: LOOKAHEAD_LENGTH_CLEAR,
  payload: {}
});

const lookaheadLengthSelected: ActionCreator<LookaheadLengthSelectedAction> = (
  length: number
) => ({
  type: LOOKAHEAD_LENGTH_SELECTED,
  payload: {
    length
  }
});

const selectedPhasePlan: ActionCreator<PhasePlanSelectedAction> = (
  selectedPhasePlan: string
) => ({
  type: PHASE_PLAN_SELECTED,
  payload: {
    selectedPhasePlan
  }
});

const phaseplanTagSelectedAction: ActionCreator<PhasePlanTagSelectedAction> = (
  tagId: string
) => ({
  type: PHASE_PLAN_TAG_SELECTED,
  payload: {
    tagId
  }
});

const phasePlanTagsSelectedAction: ActionCreator<PhasePlanTagsSelectedAction> = (
  tags: Record<string, SelectedTag>
) => ({
  type: PHASE_PLAN_TAGS_SELECTED,
  payload: {
    tags
  }
});

const phasePlanTagsClearAction: ActionCreator<PhasePlanTagsClearedAction> = () => ({
  type: PHASE_PLAN_TAGS_CLEARED,
  payload: {}
});

const phasePlanHistogramToggle: ActionCreator<PhasePlanHistogramToggle> = () => ({
  type: PHASE_PLAN_HISTOGRAM_TOGGLE,
  payload: {}
});

const phasePlanSelectedTagsClearActionAsync = () => {
  return async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>,
    getState: () => RootState
  ) => {
    dispatch(phasePlanTagsClearAction());
  };
};

const phasePlanTagsSelectedActionAsync = (
  tags: Record<string, SelectedTag>
) => {
  return async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>,
    getState: () => RootState
  ) => {
    dispatch(phasePlanTagsSelectedAction(tags));
  };
};

export type PhasePlanAction =
  | PhasePlanSelectedAction
  | LookaheadOffDaySelectedAction
  | LookaheadOffDayClearAction
  | LookaheadLengthSelectedAction
  | LookaheadLengthClearAction
  | PhasePlanTagsClearedAction
  | PhasePlanTagsSelectedAction
  | PhasePlanTagSelectedAction
  | PhasePlanFilterByTradeAction;

const selectPhasePlanAction = (phasePlanId: string) => {
  return async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>,
    getState: () => RootState
  ) => {
    return dispatch(selectedPhasePlan(phasePlanId));
  };
};

const saveConstraint = (
  type: ColumnType,
  constraint: Constraint,
  constraintId?: string
) => {
  const firebase = getFirebase();

  return async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>,
    getState: () => RootState
  ) => {
    const state = getState();
    const projectId = getProjectId(state, type);
    const phasePlanId = getLeastPlannerId(state, type);

    if (!projectId || !phasePlanId) {
      dispatch(alertActions.error('App Error'));
    }

    delete constraint['uid'];
    delete constraint['tableData'];
    delete constraint['tagCardType'];

    const dbType = getDbFromType(type);

    const entityRoute = `/projects/${projectId}/${dbType}/${phasePlanId}/constraints/`;

    const ref = firebase.database().ref(entityRoute);

    let updateFunc = refUpdate;
    let newConstraintId = constraintId;
    if (!newConstraintId) {
      newConstraintId = ref.push().key as string;
      updateFunc = refSave;
    }

    batch(() => {
      dispatch(addConstraintLoading());
      dispatch(homeActions.openCircularProgressLoading());
    });

    updateFunc(
      firebase,
      firebase.database().ref(`${entityRoute}${newConstraintId}`),
      constraint
    )
      .then(() => {
        batch(() => {
          dispatch(homeActions.closeCircularProgressLoading());
          dispatch(addConstraintStopLoading());
          dispatch(alertActions.success('Constraint Saved'));
        });
      })
      .catch((e: Error) => {
        console.log('error saving constraint', e);
        batch(() => {
          dispatch(homeActions.closeCircularProgressLoading());

          dispatch(alertActions.error('Error Saving Constraint'));
          dispatch(addConstraintStopLoading());
        });
      });
    return constraintId;
  };

  function addConstraintLoading() {
    return {
      type: constants.ADD_TRADE_LOADING,
       
    };
  }

  function addConstraintStopLoading() {
    return {
      type: constants.ADD_TRADE_STOP_LOADING,
       
    };
  }
};

const addPullDate = (pullDate: string, pushOrPull: PullOrPush) => {
  const firebase = getFirebase();

  return async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>,
    getState: () => RootState
  ) => {
    const state = getState();
    const projectId = getProjectId(state);
    const phasePlanId = getSelectedPhasePlanId(state);
    const entityRoute = `/projects/${projectId}/phases/${phasePlanId}/pullInfo`;
    const pullInfo: PullInfo = {
      pullDate: pullDate,
      pushOrPull: pushOrPull
    };

    dispatch(homeActions.openCircularProgressLoading());

    await firebase
      .database()
      .ref(`${entityRoute}`)
      .update(pullInfo);

    dispatch(homeActions.closeCircularProgressLoading());
  };
};

const savePhasePlan = (phasePlanId: string | null, phasePlanName: string) => {
  return async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>,
    getState: () => RootState
  ) => {
    dispatch(homeActions.openCircularProgressLoading());

    const firebase = getFirebase();
    const state = getState();

    const projectId = getProjectId(state);

    const entityRoute = `/projects/${projectId}/phases/`;

    const ref = firebase.database().ref(entityRoute);

    let updateFunc = refUpdate;

    let tempPhasePlanId = phasePlanId;
    if (!tempPhasePlanId) {
      tempPhasePlanId = ref.push().key;
      updateFunc = refSave;
    }

    updateFunc(
      firebase,
      firebase.database().ref(`${entityRoute}${tempPhasePlanId}`),
      {
        name: phasePlanName
      }
    )
      .then(() => {
        batch(() => {
          dispatch(homeActions.closeCircularProgressLoading());
          dispatch(selectedPhasePlan(tempPhasePlanId as string));
          dispatch(alertActions.success('Creating PhasePlan'));
        });
      })
      .catch(e => {
        console.log('error saving trades', e);
        batch(() => {
          dispatch(homeActions.closeCircularProgressLoading());

          dispatch(alertActions.error('Error Saving Phase Plan'));
        });
      });
  };
};

const downloadXERPhasePlan = () => {
  return async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>,
    getState: () => RootState
  ) => {
    dispatch(alertActions.success('Download Starting Now...'));
    const state = getState();

    const projectId = getProjectId(state);
    const phasePlanId = getSelectedPhasePlanId(state);
  };
};

function saveTrade(trade: Trade, tradeId?: string) {
  const firebase = getFirebase();

  return async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>,
    getState: () => RootState
  ) => {
    const state = getState();
    const projectId = getProjectId(state);
    const lookaheadId = getSelectedLookaheadId(state);

    if (!projectId || !lookaheadId) {
      dispatch(alertActions.error('App Error'));
    }


    delete trade['uid'];

    const entityRoute = `/projects/${projectId}/lookaheads/${lookaheadId}/trades/`;

    const ref = firebase.database().ref(entityRoute);

    let updateFunc = refUpdate;
    let newTradeId = tradeId;
    if (!newTradeId) {
      newTradeId = ref.push().key;
      updateFunc = refSave;
    }

    batch(() => {
      dispatch(addTradeLoading());
      dispatch(homeActions.openCircularProgressLoading());
    });


    firebase.database().ref(`${entityRoute}${newTradeId}`).update(clean(trade))
      .then(() => {
        batch(() => {
          dispatch(homeActions.closeCircularProgressLoading());
          dispatch(addTradeStopLoading());
          dispatch(alertActions.success('Trade Saved'));
        });
      })
      .catch((e: Error) => {
        console.error('error saving trades', e);
        batch(() => {
          dispatch(homeActions.closeCircularProgressLoading());

          dispatch(alertActions.error('Error Saving trade'));
          dispatch(addTradeStopLoading());
        });
      });
    return tradeId;
  };

  function addTradeLoading() {
    return {
      type: constants.ADD_TRADE_LOADING,
       
    };
  }

  function addTradeStopLoading() {
    return {
      type: constants.ADD_TRADE_STOP_LOADING,
       
    };
  }
}

function saveTag(
  tag: PhaseTag,
  newConstraints: Record<string, object>,
  tagId?: string
) {
  const firebase = getFirebase();

  return async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>,
    getState: () => RootState
  ) => {
    const state = getState();
    const projectId = getProjectId(state);
    const phasePlanId = getSelectedPhasePlanId(state);
    delete tag['uid'];
    delete tag['tagCardType']

    const entityRoute = `/projects/${projectId}/phases/${phasePlanId}/tags/`;
    const constraintRoute = `/projects/${projectId}/phases/${phasePlanId}/constraints/`;

    const ref = firebase.database().ref(entityRoute);

    let updateFunc = refUpdate;

    let newTagId = tagId;
    if (!newTagId) {
      newTagId = ref.push().key;
      updateFunc = refSave;
    }

    const updates: Record<string, object> = {};
    updates[`${entityRoute}${newTagId}`] = tag;

    const constraintUpdates = Object.entries(newConstraints).reduce(
      (newObj: Record<string, object>, [key, val]) => {
        newObj[`${constraintRoute}${key}`] = val;
        return newObj;
      },
      {}
    );

    dispatch(addTagLoading());

    firebase
      .database()
      .ref()
      .update({ ...updates, ...constraintUpdates })
      .then(() => {
        dispatch(addTagStopLoading());
      })
      .catch((e: Error) => {
        console.error('error saving tags', tag, updates, e);

        dispatch(alertActions.error('Error Saving Tag'));
        dispatch(addTagStopLoading());
      });
    return tagId;
  };

  function addTagLoading() {
    return {
      type: constants.ADD_PHASE_TAG_LOADING,
       
    };
  }

  function addTagStopLoading() {
    return {
      type: constants.ADD_PHASE_TAG_STOP_LOADING,
       
    };
  }
}
export interface TagChangeInterface {
  newColumn: string | number;
  tagCardType: TagType;
  newIndex: string | number
}

export interface VarianceTagChangeInterface {
  newColumn: string | number;
  tagCardType: TagType;
  newIndex: string | number;
  commitmentId: string;
}

export type TagsChangesInterface = Record<string, TagChangeInterface>;

export const getDbFromTagType = (tagType: TagType, isMilestoneTag?: Boolean) => {
  if (tagType === TagType.PhasePlan && !!isMilestoneTag) {
    return 'milestone_tags';
  }

  if ((tagType === TagType.PhasePlan  || tagType == 'PhasePlanTagType') && !isMilestoneTag ) {
    return 'tags';
  }

  if ((tagType === TagType.Lookahead || tagType == 'LookaheadTagType' )&& !!isMilestoneTag) {
    return 'milestone_tags';
  }

  if (tagType === TagType.Lookahead || tagType == 'LookaheadTagType') {
    return 'tags';
  }

  if (tagType === TagType.Constraint) {
    return 'constraints';
  }

  throw new Error('Invalid getDbFrromTagType', type);
};

function changeTagColumn(changes: TagsChangesInterface) {
  const firebase = getFirebase();

  return async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>,
    getState: () => RootState
  ) => {
    const state = getState();
    const projectId = getProjectId(state);
    const phasePlanId = getSelectedPhasePlanId(state);

    const updates: Record<string, object> = {};

    Object.entries(changes).forEach(([key, value]) => {
      const { newColumn, newIndex, tagCardType } = value;

      const tagType = getDbFromTagType(tagCardType);
      updates[
        `/projects/${projectId}/phases/${phasePlanId}/${tagType}/${key}/column`
      ] = parseInt(newColumn, 10);
      updates[
        `/projects/${projectId}/phases/${phasePlanId}/${tagType}/${key}/indexId`
      ] = parseInt(newIndex, 10);
    });

    batch(() => {
      dispatch(changeTagLoading());
      dispatch(updateColumns(changes));
      dispatch(phasePlanTagsClearAction());
    });

    await firebase
      .database()
      .ref()
      .update(updates)
      .then(() => {
        dispatch(changeTagStopLoading());
      })
      .catch((e: Error) => {
        console.error('error changing tags', e);
        batch(() => {
          dispatch(alertActions.error('Error Moving Tag'));
          dispatch(changeTagStopLoading());
        });
        bugsnagClient.notify(e)
      });
  };

  function updateColumns(changes: Record<string, number>) {
    return {
      type: constants.CHANGE_PHASE_TAG_COLUMN,
      payload: changes
    };
  }

  function changeTagLoading() {
    return {
      type: constants.CHANGE_PHASE_TAG_COLUMN_LOADING,
       
    };
  }

  function changeTagStopLoading() {
    return {
      type: constants.ADD_PHASE_TAG_STOP_LOADING,
       
    };
  }
}

function deleteTagColumn(changes: TagsChangesInterface) {
  const firebase = getFirebase();

  return async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>,
    getState: () => RootState
  ) => {
    const state = getState();
    const projectId = getProjectId(state);
    const phasePlanId = getSelectedPhasePlanId(state);

    const updates: Record<string, null> = {};

    Object.entries(changes).forEach(([key, value]) => {
      const { tagCardType } = value;
      const tagType = getDbFromTagType(tagCardType);

      updates[
        `/projects/${projectId}/phases/${phasePlanId}/${tagType}/${key}/column`
      ] = null;
    });

    batch(() => {
      dispatch(phasePlanTagsClearAction());

      dispatch(changeTagLoading());
    });
    firebase
      .database()
      .ref()
      .update({ ...updates })
      .then(() => {
        dispatch(changeTagStopLoading());
      })
      .catch((e: Error) => {
        console.error('error changing tags', e);
        batch(() => {
          dispatch(alertActions.error('Error Moving Tag'));
          dispatch(changeTagStopLoading());
          dispatch(phasePlanTagsClearAction());
        });
        bugsnagClient.notify(e)
      });
    return;
  };

  function changeTagLoading() {
    return {
      type: constants.ADD_PHASE_TAG_LOADING,
       
    };
  }

  function changeTagStopLoading() {
    return {
      type: constants.ADD_PHASE_TAG_STOP_LOADING,
       
    };
  }
}

function sendMileStoneForLocation(location: string, milestone: number) {
  const firebase = getFirebase();

  return async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>,
    getState: () => RootState
  ) => {
    const projectId = getProjectId(getState());
    const phasePlanId = getSelectedPhasePlanId(getState());

    const entityRoute = `/projects/${projectId}/phases/${phasePlanId}/locations/`;

    const locationKey = sh.unique(location);

    dispatch(changeLocationLoading());
    refUpdate(
      firebase,
      firebase.database().ref(`${entityRoute}${locationKey}`),
      {
        location,
        milestone
      }
    )
      .then(() => {
        dispatch(changeLocationStopLoading());
      })
      .catch(e => {
        console.error('error changing location', e);

        dispatch(alertActions.error('Error Adding Milestone'));
        dispatch(changeLocationStopLoading());
      });
    return location;
  };

  function changeLocationLoading() {
    return {
      type: constants.ADD_PHASE_TAG_LOADING,
       
    };
  }

  function changeLocationStopLoading() {
    return {
      type: constants.ADD_PHASE_TAG_STOP_LOADING,
       
    };
  }
}

const onTagClicked = (event: any, tagId: string, selectedTag: SelectedTag) => {
  return async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>,
    getState: () => RootState
  ) => {
    if (event.defaultPrevented) {
      return;
    }

    event.preventDefault();

    const isSelectMultPressed = isSelectMultiPressed(getState());

    const selectedTags: Record<string, SelectedTag> = getSelectedPhasePlanTags(
      getState()
    );

    let newSelectedTags: Record<string, SelectedTag> = { ...selectedTags };

    if (!isSelectMultPressed) {
      newSelectedTags = {};
    }

    if (!!newSelectedTags[tagId]) {
      delete newSelectedTags[tagId];
    } else {
      newSelectedTags[tagId] = selectedTag;
    }

    dispatch(phasePlanTagsSelectedAction(newSelectedTags));
  };
};

const onTagDoubleClicked = (
  event: any,
  tagId: string,
  selectedTag: SelectedTag
) => {
  return async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>,
    getState: () => RootState
  ) => {
    if (event.defaultPrevented) {
      return;
    }

    event.preventDefault();

    if (selectedTag.tagType === TagType.PhasePlan) {
      dispatch(dialogActions.showTagDialog(tagId));
    }

    if (selectedTag.tagType === TagType.Lookahead) {
      dispatch(lookaheadActions.lookaheadDialogOpen(tagId));
    }

    if (selectedTag.tagType === TagType.Constraint) {
      dispatch(dialogActions.openConstraintDialog(tagId));
    }
  };
};

const openConstraintDialogAsync = (constraint?: string) => {
  return async (
    dispatch: ThunkDispatch<{}, {}, AnyAction>,
    getState: () => RootState
  ) => {};
};

export const phasePlanActions = {
  saveConstraint,
  saveTrade,
  saveTag,
  changeTagColumn,
  deleteTagColumn,
  sendMileStoneForLocation,
  selectPhasePlanAction,
  savePhasePlan,
  downloadXERPhasePlan,
  lookaheadOffDaySelected,
  lookaheadOffDayClear,
  lookaheadLengthSelected,
  lookaheadLengthClear,
  phaseplanTagSelectedAction,
  onTagClicked,
  onTagDoubleClicked,
  phasePlanHistogramToggle,
  phasePlanSelectedTagsClearActionAsync,
  phasePlanTagsSelectedActionAsync,
  addPullDate,
  phasePlanFilterByTrade,
  lookaheadOffDayBulkSelected
};



// WEBPACK FOOTER //
// ./src/actions/phaseplan.actions.ts